import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { formatDateDisplay } from "../../lib/utils";
import { Plus, X, ChevronDown, ChevronRight, Clock, Pencil, XCircle, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import AdminOrderPopup from "./AdminOrderPopup";
import AdminOrderEditPopup from "./AdminOrderEditPopup";
import DateInput from "./DateInput";

const STATUS_COLORS = {
  placed:     "bg-gold/20 text-gold",
  processing: "bg-sky-500/20 text-sky-300",
  shipped:    "bg-violet-500/20 text-violet-300",
  delivered:  "bg-emerald-500/20 text-emerald-300",
  cancelled:  "bg-rose-500/20 text-rose-300",
};

const emptyForm = {
  product_id: "",
  product_name: "",
  customer_name: "",
  customer_email: "",
  customer_phone: "",
  quantity: 1,
  comments: "",
  release_date: "",
  booking_amount: "",
  price_offered: "",
  delivery_required: false,
  delivery_included: false,
};

function PreorderForm({ form, setForm, products: selectableProducts, onProductChange, onSave, onClose, saving, isEdit }) {
  return (
    <div className="fixed inset-0 z-50 bg-navy-900/80 backdrop-blur flex items-center justify-center p-4">
      <div className="bg-navy-800 rounded-2xl p-6 max-w-lg w-full neon-border max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl font-black uppercase tracking-tight">{isEdit ? "Edit Preorder" : "New Preorder"}</h2>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-gold"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          {!isEdit && (
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Product</span>
              <select
                value={form.product_id}
                onChange={(e) => onProductChange(e.target.value)}
                className="w-full mt-1 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold/60"
              >
                <option value="">— Select a product —</option>
                {selectableProducts.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} ({p.platform})</option>
                ))}
              </select>
            </label>
          )}
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Product Name {!isEdit && "(if not in list)"}</span>
            <input
              value={form.product_name}
              onChange={(e) => setForm({ ...form, product_name: e.target.value })}
              readOnly={isEdit}
              className={`w-full mt-1 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold/60 ${isEdit ? "opacity-50 cursor-not-allowed" : ""}`}
              placeholder="Manual product name"
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Release Date</span>
            <DateInput
              value={form.release_date}
              onChange={(e) => setForm({ ...form, release_date: e.target.value })}
              className="w-full mt-1 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold/60"
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Customer Name *</span>
            <input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="w-full mt-1 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold/60" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Phone *</span>
            <input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className="w-full mt-1 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold/60" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Email</span>
            <input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="w-full mt-1 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold/60" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Quantity</span>
            <input type="number" min={1} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) })} className="w-full mt-1 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold/60" />
          </label>

          {/* Pricing */}
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Booking Amount Collected</span>
              <input type="number" min={0} value={form.booking_amount} onChange={(e) => setForm({ ...form, booking_amount: e.target.value })} placeholder="e.g. 500" className="w-full mt-1 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold/60" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Total Price Offered</span>
              <input type="number" min={0} value={form.price_offered} onChange={(e) => setForm({ ...form, price_offered: e.target.value })} placeholder="e.g. 4999" className="w-full mt-1 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold/60" />
            </label>
          </div>

          {/* Delivery */}
          <div className="border border-gold/15 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-slate-200">Delivery Required?</span>
              <button
                type="button"
                onClick={() => setForm({ ...form, delivery_required: !form.delivery_required, delivery_included: false })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.delivery_required ? "bg-gold" : "bg-navy-600"}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.delivery_required ? "translate-x-6" : "translate-x-1"}`} />
              </button>
            </div>
            {form.delivery_required && (
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, delivery_included: false })}
                  className={`flex-1 text-xs py-2 rounded-full border font-bold uppercase tracking-wider transition ${!form.delivery_included ? "bg-gold text-navy-900 border-gold" : "border-gold/30 text-slate-400 hover:border-gold/50"}`}
                >
                  Extra Charge
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, delivery_included: true })}
                  className={`flex-1 text-xs py-2 rounded-full border font-bold uppercase tracking-wider transition ${form.delivery_included ? "bg-gold text-navy-900 border-gold" : "border-gold/30 text-slate-400 hover:border-gold/50"}`}
                >
                  Included in Price
                </button>
              </div>
            )}
          </div>

          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Comments / Notes</span>
            <textarea value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} rows={3} className="w-full mt-1 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold/60" placeholder="Any notes, preferences, special requests..." />
          </label>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 border border-gold/30 text-slate-300 font-bold uppercase tracking-wider text-xs py-3 rounded-full hover:bg-navy-700">Cancel</button>
          <button onClick={onSave} disabled={saving} className="flex-1 bg-gold text-navy-900 font-extrabold uppercase tracking-wider text-xs py-3 rounded-full hover:bg-gold-light disabled:opacity-50">
            {saving ? "Saving..." : isEdit ? "Update Preorder" : "Save Preorder"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminPreorders() {
  const [preorders, setPreorders] = useState([]);
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingPo, setEditingPo] = useState(null); // preorder being edited
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);
  const [confirmCompleteAll, setConfirmCompleteAll] = useState(null); // product group to complete + clear
  const [confirmRemoveCompleted, setConfirmRemoveCompleted] = useState(null); // product group to clear completed rows from
  const [activeOrder, setActiveOrder] = useState(null); // linked order shown in AdminOrderPopup (fulfillment)
  const [editingOrder, setEditingOrder] = useState(null); // linked order shown in AdminOrderEditPopup (details)
  const [orderLoading, setOrderLoading] = useState(false);

  const load = async () => {
    const [pr, prods] = await Promise.all([
      api.get("/admin/preorders"),
      api.get("/admin/products/all"),
    ]);
    setPreorders(pr.data);
    setProducts(prods.data);
  };

  useEffect(() => { load(); }, []);

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const selectableProducts = [...products]
    .sort((a, b) => {
      const aFuture = a.release_date && new Date(a.release_date) >= today;
      const bFuture = b.release_date && new Date(b.release_date) >= today;
      if (aFuture && !bFuture) return -1;
      if (!aFuture && bFuture) return 1;
      if (aFuture && bFuture) return new Date(a.release_date) - new Date(b.release_date);
      return (a.name || "").localeCompare(b.name || "");
    });

  const grouped = preorders.reduce((acc, po) => {
    const key = po.product_id || po.product_name;
    if (!acc[key]) acc[key] = { product_name: po.product_name, product_id: po.product_id, release_date: po.release_date, orders: [] };
    acc[key].orders.push(po);
    return acc;
  }, {});

  const onProductChange = (productId) => {
    const p = products.find((x) => x.id === productId);
    setForm({ ...form, product_id: productId, product_name: p ? p.name : "", release_date: p?.release_date || "" });
  };

  const openNew = () => {
    setEditingPo(null);
    setForm({ ...emptyForm });
    setShowForm(true);
  };

  const openEdit = (po) => {
    setEditingPo(po);
    setForm({
      product_id: po.product_id || "",
      product_name: po.product_name || "",
      customer_name: po.customer_name || "",
      customer_email: po.customer_email || "",
      customer_phone: po.customer_phone || "",
      quantity: po.quantity || 1,
      comments: po.comments || "",
      release_date: po.release_date || "",
      booking_amount: po.booking_amount ?? "",
      price_offered: po.price_offered ?? "",
      delivery_required: po.delivery_required || false,
      delivery_included: po.delivery_included || false,
    });
    setShowForm(true);
  };

  const onSave = async () => {
    if (!form.customer_name || !form.customer_phone || !form.product_name) {
      toast.error("Fill in customer name, phone and product");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        booking_amount: form.booking_amount !== "" ? Number(form.booking_amount) : null,
        price_offered: form.price_offered !== "" ? Number(form.price_offered) : null,
      };
      if (editingPo) {
        await api.patch(`/admin/preorders/${editingPo.id}`, payload);
        toast.success("Preorder updated");
      } else {
        await api.post("/admin/preorders", { ...payload, source: "admin" });
        toast.success("Preorder added");
      }
      setShowForm(false);
      setEditingPo(null);
      setForm({ ...emptyForm });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const openRow = async (po) => {
    if (orderLoading) return;
    if (!po.order_id) {
      openEdit(po);
      return;
    }
    setOrderLoading(true);
    try {
      const r = await api.get(`/orders/${po.order_id}`);
      setActiveOrder(r.data);
    } catch {
      toast.error("Could not load the linked order");
    } finally { setOrderLoading(false); }
  };

  const openEditRow = async (po) => {
    if (orderLoading) return;
    if (!po.order_id) {
      openEdit(po);
      return;
    }
    setOrderLoading(true);
    try {
      const r = await api.get(`/orders/${po.order_id}`);
      setEditingOrder(r.data);
    } catch {
      toast.error("Could not load the linked order");
    } finally { setOrderLoading(false); }
  };

  const onCompleteAllConfirmed = async () => {
    if (!confirmCompleteAll) return;
    try {
      const r = await api.post(`/admin/preorders/product/${confirmCompleteAll.product_id}/complete-all`);
      toast.success(`Marked ${r.data.completed} preorder(s) delivered and cleared`);
      setConfirmCompleteAll(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Complete all failed");
    }
  };

  const onRemoveCompletedConfirmed = async () => {
    if (!confirmRemoveCompleted) return;
    try {
      const r = await api.post(`/admin/preorders/product/${confirmRemoveCompleted.product_id}/remove-completed`);
      toast.success(`Removed ${r.data.removed} completed preorder(s)`);
      setConfirmRemoveCompleted(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove completed failed");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-gold mb-2">Pre-Sales</div>
          <h1 className="text-3xl font-black uppercase tracking-tighter">Preorders</h1>
        </div>
        <button
          onClick={openNew}
          className="bg-gold text-navy-900 font-extrabold uppercase tracking-wider text-xs px-5 py-2.5 rounded-full hover:bg-gold-light flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> New Preorder
        </button>
      </div>

      {Object.keys(grouped).length === 0 && (
        <div className="bg-navy-700 rounded-xl neon-border p-10 text-center text-slate-400">No preorders yet.</div>
      )}

      <div className="space-y-3">
        {Object.entries(grouped).map(([key, group]) => {
          const isOpen = expandedItem === key;
          const activeOrders = group.orders.filter((o) => o.status !== "cancelled");
          const totalUnits = activeOrders.reduce((s, o) => s + (o.quantity || 1), 0);
          const completedOrders = group.orders.filter((o) => o.status === "completed");
          const releaseDate = group.release_date || group.orders[0]?.release_date;
          return (
            <div key={key} className="bg-navy-700 rounded-xl neon-border overflow-hidden">
              <button
                onClick={() => setExpandedItem(isOpen ? null : key)}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-navy-800/50 transition"
              >
                <div className="flex items-center gap-3 min-w-0">
                  {isOpen ? <ChevronDown className="w-4 h-4 text-gold shrink-0" /> : <ChevronRight className="w-4 h-4 text-gold shrink-0" />}
                  <span className="font-bold text-sm truncate">{group.product_name}</span>
                  {releaseDate && (
                    <span className="text-[10px] text-slate-400 flex items-center gap-1 shrink-0">
                      <Clock className="w-3 h-3" />
                      {formatDateDisplay(releaseDate)}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  {group.product_id && completedOrders.length > 0 && (
                    <span
                      role="button"
                      onClick={(e) => { e.stopPropagation(); setConfirmRemoveCompleted(group); }}
                      title="Clear out preorder rows already marked completed, without touching their orders"
                      className="flex items-center gap-1 px-2 py-1 rounded-full border border-slate-500/30 text-slate-400 text-[10px] font-bold uppercase tracking-wider hover:bg-slate-500/10 hover:text-slate-300 transition"
                    >
                      <X className="w-3 h-3" /> Remove Completed
                    </span>
                  )}
                  {group.product_id && activeOrders.length > 0 && (
                    <span
                      role="button"
                      onClick={(e) => { e.stopPropagation(); setConfirmCompleteAll(group); }}
                      title="Mark every active preorder here as delivered and clear this group from the list"
                      className="flex items-center gap-1 px-2 py-1 rounded-full border border-emerald-500/30 text-emerald-400 text-[10px] font-bold uppercase tracking-wider hover:bg-emerald-500/10 transition"
                    >
                      <CheckCheck className="w-3 h-3" /> Complete All
                    </span>
                  )}
                  <span className="text-gold font-black flex items-baseline gap-1.5">
                    <span className="tabular-nums inline-block text-right" style={{ minWidth: "2.2ch" }}>{totalUnits}</span>
                    units
                  </span>
                </div>
              </button>

              {isOpen && (
                <div className="border-t border-gold/10 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-navy-800 text-[10px] uppercase tracking-[0.2em] text-gold/80">
                      <tr>
                        <th className="text-left px-4 py-2.5">Order ID</th>
                        <th className="text-left px-4 py-2.5">Customer</th>
                        <th className="text-left px-4 py-2.5">Phone</th>
                        <th className="text-right px-4 py-2.5">Booked ₹</th>
                        <th className="text-right px-4 py-2.5">Total</th>
                        <th className="text-left px-4 py-2.5">Status</th>
                        <th className="text-left px-4 py-2.5">Payment</th>
                        <th className="text-left px-4 py-2.5">Date</th>
                        <th className="px-4 py-2.5"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.orders.map((po) => (
                        <React.Fragment key={po.id}>
                          <tr
                            onClick={() => openRow(po)}
                            className={`border-t border-gold/10 hover:bg-navy-800/40 cursor-pointer ${po.order_status === "cancelled" ? "opacity-50" : ""}`}
                          >
                            <td className="px-4 py-3 font-mono text-xs text-slate-300">
                              <div className="flex items-center gap-2 flex-wrap">
                                {po.order_id || "—"}
                                <span className="bg-gold/20 text-gold text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded">PRE-ORDER</span>
                              </div>
                              {po.release_date && (
                                <div className="text-[10px] text-gold/60 mt-0.5">Releases {formatDateDisplay(po.release_date)}</div>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="font-semibold">{po.customer_name}</div>
                              {po.customer_email && <div className="text-xs text-slate-400">{po.customer_email}</div>}
                            </td>
                            <td className="px-4 py-3 text-slate-300">{po.customer_phone || "—"}</td>
                            <td className="px-4 py-3 text-right font-bold text-gold">
                              {po.booking_amount != null ? `₹${po.booking_amount.toLocaleString()}` : "—"}
                            </td>
                            <td className="px-4 py-3 text-right text-slate-200">
                              {po.price_offered != null ? `₹${po.price_offered.toLocaleString()}` : "—"}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${STATUS_COLORS[po.order_status] || "bg-slate-700 text-slate-300"}`}>
                                {po.order_status || "placed"}
                              </span>
                            </td>
                            <td className="px-4 py-3 uppercase text-xs">{po.payment_method || "—"}</td>
                            <td className="px-4 py-3 text-xs text-slate-400">{formatDateDisplay(po.created_at)}</td>
                            <td className="px-4 py-3">
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); openEditRow(po); }}
                                title="Edit order details"
                                className="p-1.5 text-slate-400 hover:text-gold transition"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                            </td>
                          </tr>
                          {po.comments && (
                            <tr className="border-t border-gold/5 bg-navy-800/30">
                              <td colSpan={9} className="px-4 py-2 text-xs text-slate-400 italic">
                                💬 {po.comments}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {showForm && (
        <PreorderForm
          form={form}
          setForm={setForm}
          products={selectableProducts}
          onProductChange={onProductChange}
          onSave={onSave}
          onClose={() => { setShowForm(false); setEditingPo(null); setForm({ ...emptyForm }); }}
          saving={saving}
          isEdit={!!editingPo}
        />
      )}

      {activeOrder && (
        <AdminOrderPopup
          order={activeOrder}
          onClose={() => setActiveOrder(null)}
          onUpdated={load}
        />
      )}

      {editingOrder && (
        <AdminOrderEditPopup
          order={editingOrder}
          onClose={() => setEditingOrder(null)}
          onUpdated={load}
        />
      )}

      {/* Complete All confirmation dialog */}
      {confirmCompleteAll && (
        <div className="fixed inset-0 z-50 bg-navy-900/80 backdrop-blur flex items-center justify-center p-4">
          <div className="bg-navy-800 rounded-2xl p-6 max-w-sm w-full neon-border">
            <div className="flex items-center gap-2 mb-2">
              <CheckCheck className="w-5 h-5 text-emerald-400" />
              <h3 className="text-lg font-black uppercase tracking-tight text-emerald-400">Complete All Preorders?</h3>
            </div>
            <p className="text-sm text-slate-300 mb-1">
              Release day is here for <span className="font-bold text-white">{confirmCompleteAll.product_name}</span> — you've handed over all the units.
            </p>
            <p className="text-xs text-slate-400 mb-5">
              This marks every active preorder here as completed, sets their linked orders to <span className="font-semibold text-white">delivered</span>, and clears the whole group from this list. This is not a cancellation — it means the orders are done.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmCompleteAll(null)}
                className="flex-1 border border-gold/30 text-slate-300 font-bold uppercase tracking-wider text-xs py-3 rounded-full hover:bg-navy-700"
              >
                Not Yet
              </button>
              <button
                onClick={onCompleteAllConfirmed}
                className="flex-1 bg-emerald-500 text-white font-extrabold uppercase tracking-wider text-xs py-3 rounded-full hover:bg-emerald-400"
              >
                Yes, Complete All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Completed confirmation dialog */}
      {confirmRemoveCompleted && (
        <div className="fixed inset-0 z-50 bg-navy-900/80 backdrop-blur flex items-center justify-center p-4">
          <div className="bg-navy-800 rounded-2xl p-6 max-w-sm w-full neon-border">
            <div className="flex items-center gap-2 mb-2">
              <X className="w-5 h-5 text-slate-300" />
              <h3 className="text-lg font-black uppercase tracking-tight text-slate-100">Remove Completed?</h3>
            </div>
            <p className="text-sm text-slate-300 mb-1">
              Clears out preorder rows for <span className="font-bold text-white">{confirmRemoveCompleted.product_name}</span> that are already marked completed.
            </p>
            <p className="text-xs text-slate-400 mb-5">
              This only tidies up leftover completed rows — it does <span className="font-semibold text-white">not</span> change any order status. Active (not-yet-completed) preorders in this group are left untouched.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmRemoveCompleted(null)}
                className="flex-1 border border-gold/30 text-slate-300 font-bold uppercase tracking-wider text-xs py-3 rounded-full hover:bg-navy-700"
              >
                Keep It
              </button>
              <button
                onClick={onRemoveCompletedConfirmed}
                className="flex-1 bg-slate-600 text-white font-extrabold uppercase tracking-wider text-xs py-3 rounded-full hover:bg-slate-500"
              >
                Yes, Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
